<template>
	<div>
		<div class="error-page" v-if="isMaintenanceError">
			<Meta http-equiv="refresh" :content="isBuildMetaError ? '2' : '60'" />
			<Title>{{ maintenanceError.title }}</Title>
			<div style="text-align: center">
				<br /><img src="/images/logo-mail.png" width="100" style="width: 100px; height: auto" /><br /><br />
				{{ maintenanceError.description }}
				<div style="padding-top: 30px">
					<button @click="reloadPage" style="margin: auto">
						<span>{{ maintenanceError.refresh }}</span>
					</button>
				</div>
			</div>
		</div>
		<div v-else-if="isUnauthorizedRecoveryError">
			<BaseThemeUiLoading mode="fullpage" />
		</div>
		<div v-else class="error-page">
			<div v-if="statusCode === 500 && isProduction" class="friendly-error">
				<br /><img src="/images/logo-mail.png" width="100" style="width: 100px; height: auto" /><br /><br />
				{{ friendlyError.description }}
				<div class="error-page-actions">
					<button @click="reloadPage">
						<span>{{ friendlyError.refresh }}</span>
					</button>
					<button @click="handleError">
						<span>{{ friendlyError.home }}</span>
					</button>
				</div>
			</div>
			<template v-else>
				<Title>{{ error.statusCode }}</Title>
				<div style="text-align: left">
					<h1>Error {{ error.statusCode }}</h1>
					<h2 v-if="error.statusMessage">{{ error.statusMessage }}</h2>
					<div v-if="error.stack" v-html="error.stack" />
					<br />
					<button @click="handleError">
						<span>{{ backToHome }}</span>
					</button>
				</div>
			</template>
		</div>
	</div>
</template>

<style scoped>
	.error-page {
		padding: 2%;
		font-size: 15px;
		line-height: 1.4;
		text-align: center;
		position: fixed;
		top: 0;
		right: 0;
		bottom: 0;
		left: 0;
		background: #fff;
		align-items: center;
		justify-content: center;
		z-index: 99999999999;
	}
	.error-page-actions {
		display: flex;
		flex-wrap: wrap;
		justify-content: center;
		gap: 10px;
		padding-top: 20px;
	}
	img {
		height: auto;
		max-width: 200px;
	}
	h1 {
		font-size: 30px;
		padding: 0 0 20px;
		margin: 0;
		font-weight: bold;
	}
	h2 {
		font-size: 25px;
		margin: 0;
		padding: 0 0 10px;
		font-weight: normal;
	}
</style>

<script setup>
	const config = useAppConfig();
	const nuxtApp = useNuxtApp();
	const token = useToken();
	const {captureErrorPage} = useSentryTracking();
	const storage = useLocalStorage();
	const props = defineProps({
		error: Object,
	});

	const isProduction = import.meta.env.PROD;
	const statusCode = computed(() => {
		const value = props.error.statusCode;
		return value === null || value === undefined || value === '' ? Number.NaN : Number(value);
	});
	const errorMessage = computed(() => String(props.error.message || ''));
	const isBuildMetaError = computed(() => errorMessage.value.includes('/_nuxt/builds/meta/'));
	const isChunkLoadError = computed(() => {
		const message = errorMessage.value.toLowerCase();
		const name = String(props.error.name || '').toLowerCase();
		return name.includes('chunkloaderror') || ['importing a module script failed', 'failed to fetch dynamically imported module', 'error loading dynamically imported module', 'loading chunk', 'unable to preload css'].some(pattern => message.includes(pattern));
	});
	const isMaintenanceError = computed(() => statusCode.value === 503 || isBuildMetaError.value || isChunkLoadError.value);
	const isUnauthorizedRecoveryError = computed(() => statusCode.value === 401 || (statusCode.value === 404 && errorMessage.value === 'unauthorized'));
	const shouldRedirectTo404 = computed(() => isProduction && statusCode.value !== 500 && !isMaintenanceError.value && !isUnauthorizedRecoveryError.value);
	const errorReason = computed(() => {
		if (statusCode.value === 503) return 'maintenance-503';
		if (isBuildMetaError.value) return 'build-meta';
		if (isChunkLoadError.value) return 'chunk-load';
		if (statusCode.value === 401) return 'auth-401';
		if (statusCode.value === 404 && errorMessage.value === 'unauthorized') return 'auth-hidden-404';
		if (statusCode.value === 500) return 'generic-500';
		if (statusCode.value === 404) return 'not-found-404';
		if (Number.isFinite(statusCode.value)) return `http-${statusCode.value}`;
		return 'unknown';
	});
	const errorDisplay = computed(() => {
		if (isMaintenanceError.value) return 'maintenance';
		if (isUnauthorizedRecoveryError.value) return 'auth-recovery';
		if (shouldRedirectTo404.value) return 'redirect-404';
		if (statusCode.value === 500 && isProduction) return 'friendly';
		return 'technical';
	});

	const backToHome = computed(() => {
		return config.lang == 'hr' ? 'Povratak na naslovnicu' : 'Back to homepage';
	});
	const maintenanceError = computed(() => {
		if (config.lang == 'si') {
			return {
				title: 'Nadgradnja',
				description: 'Splet se posodablja. Kmalu bomo spet na voljo.',
				refresh: 'Osveži spletno stran',
			};
		}
		if (['hr', 'ba'].includes(config.lang)) {
			return {
				title: 'Nadogradnja',
				description: 'Web stranica je u procesu ažuriranja. Uskoro smo ponovno dostupni.',
				refresh: 'Osvježi stranicu',
			};
		}
		return {
			title: 'Maintenance',
			description: 'The website is being updated. We will be available again soon.',
			refresh: 'Reload page',
		};
	});
	const friendlyError = computed(() => {
		if (config.lang == 'si') {
			return {
				description: 'Pri prikazu strani je prišlo do napake.',
				refresh: 'Osveži stran',
				home: 'Nazaj na začetno stran',
			};
		}
		if (['hr', 'ba'].includes(config.lang)) {
			return {
				description: 'Došlo je do pogreške u prikazu stranice.',
				refresh: 'Osvježi stranicu',
				home: 'Povratak na naslovnicu',
			};
		}
		return {
			description: 'An error occurred while displaying the page.',
			refresh: 'Refresh page',
			home: 'Back to homepage',
		};
	});

	captureErrorPage('NuxtError', {
		status_code: props.error.statusCode,
		status_message: props.error.statusMessage,
		error_name: props.error.name,
		message: props.error.message,
		url: props.error.url,
		reason: errorReason.value,
		display: errorDisplay.value,
	});

	if (statusCode.value === 503) {
		// Only attempt to check maintenance status on client side to avoid server-side issues and if we're not already in a reload loop
		if (import.meta.client && !storage.get('maintenance_check_attempted', {storage: 'session', raw: true})) {
			// Mark that we've attempted a maintenance check to prevent loops
			storage.create('maintenance_check_attempted', 'true', {storage: 'session', raw: true});

			try {
				const maintenance = await $fetch(`${nuxtApp.$host}/${config.api}/${config.apiVersion}/misc/is-maintenance/`, {
					headers: {
						'Authorization': 'Bearer ' + token.get(),
					},
				});

				// If maintenance check succeeds and maintenance is not active, reload once
				if (!maintenance.success && import.meta.client) {
					setTimeout(() => {
						storage.remove('maintenance_check_attempted', {storage: 'session'});
						window.location.reload();
					}, 5000);
				}
			} catch (error) {
				// If the maintenance check itself fails (likely due to 503) - don't reload
				console.log('Maintenance mode detected');
			}
		}
	}

	// unauthorized API calls. Handle tokens and reload page
	if (statusCode.value === 401) {
		await token.generateServerToken();
		token.removeToken();
		reloadNuxtApp({ttl: 2000});
	}

	// Handle unauthorized (hidden) pages
	let timeout;
	if (statusCode.value === 404 && isUnauthorizedRecoveryError.value && import.meta.client) {
		if (timeout) clearTimeout(timeout);
		if (props.error.url) {
			timeout = setTimeout(() => {
				return navigateTo(props.error.url);
			}, 500);
		}
	}

	function reloadPage() {
		// Clear maintenance check flag before reloading
		if (import.meta.client) {
			storage.remove('maintenance_check_attempted', {storage: 'session'});
		}
		return window.location.reload();
	}

	// Do not mask 500 errors as 404: known chunk failures use the maintenance
	// branch above, while remaining 500 errors use the friendly fallback.
	if (shouldRedirectTo404.value) {
		await navigateTo('/404/');
	}

	function handleError() {
		if (import.meta.client) {
			storage.remove('maintenance_check_attempted', {storage: 'session'});
			return window.location.replace('/');
		}
	}
</script>
